import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { useStore, useTranslation, useUpdateEffect } from '../../../../../hooks';
import { FIVE_MINUTES, PascalFortuneMasterGoatGameId, PascalFortunePotsGameId, PascalGodOfAtlantisGameId, T } from '../../../../../constants';
import { CashbackInfo } from '../../../../../models';
import { WidgetProgress } from './widget-progress';

interface RewardsWidgetProps {
  onClick: () => void;
  onClaimClick: () => void;
  cashbackInfo: CashbackInfo;
}

export const RewardsWidget: React.FC<RewardsWidgetProps> = observer(({
  cashbackInfo,
  onClick,
  onClaimClick
}) => {

  const rewardsEndingNotifiedRef = React.useRef(false);
  const claimReadyNotifiedRef = React.useRef(false);

  const t = useTranslation();
  const { rewardsNotificationStore, gameStore } = useStore();
  const { gameId } = gameStore;
  const { activeNotification } = rewardsNotificationStore;

  const { currentLevel, progress, claimLimitExceeded, levels, startDate, endDate } = cashbackInfo;

  const [highlighting, setHighlighting] = useState(false);
  const [simulatedProgress, setSimulatedProgress] = useState(progress);

  useEffect(() => {
    notifyClaimReady();
    const interval = setInterval(() => {
      notifyRewardsEnding();
    }, FIVE_MINUTES);
    return () => clearInterval(interval);
  }, []);

  useUpdateEffect(() => {
    setSimulatedProgress(progress);
    setHighlighting(true);
    setTimeout(() => {
      setHighlighting(false);
    }, 1000);
  }, [progress]);

  useUpdateEffect(() => {
    setSimulatedProgress(100);
    setTimeout(() => {
      setSimulatedProgress(progress);
    }, 750);
  }, [currentLevel]);

  const hasClaim = useMemo(() => {
    return !claimLimitExceeded && levels.some(({ isCompleted, isClaimed }) => isCompleted && !isClaimed);
  }, [claimLimitExceeded, levels]);

  const notifyClaimReady = useCallback(() => {
    if (hasClaim && !claimReadyNotifiedRef.current) {
      rewardsNotificationStore.enqueue(t(T.REWARDS_PRIZE_READY_NOTIFICATION));
      claimReadyNotifiedRef.current = true;
    }
  }, [hasClaim, t]);

  const notifyRewardsEnding = useCallback(() => {
    if (rewardsEndingNotifiedRef.current || claimLimitExceeded) return;
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    const duration = end - start;
    const timePassed = Date.now() - start;
    const isEnding = timePassed / duration >= 0.8; // notify when 80% of the time has passed
    if (isEnding) {
      rewardsNotificationStore.enqueue(t(T.REWARDS_ENDING_NOTIFICATION));
      rewardsEndingNotifiedRef.current = true;
    }
  }, [startDate, endDate, claimLimitExceeded, t]);

  const handleClaimClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (hasClaim) {
      onClaimClick();
    }
  }, [hasClaim, onClaimClick]);
  const isAtlantisGame = gameId === PascalGodOfAtlantisGameId;
  const isFortuneMasterGoatGame = gameId === PascalFortuneMasterGoatGameId;
  const isFortunePotsGame = gameId === PascalFortunePotsGameId;
  const notification = activeNotification?.message || '';
  return (
    <div
      onClick={onClick}
      className={`detailed-wrapper ${isAtlantisGame ? 'atlantis' : isFortuneMasterGoatGame ? 'fortune-master-goat' : isFortunePotsGame ? 'fortune-pots' : ''}`}
    >
      <div className={`detailed-svg-wrapper ${hasClaim ? 'active' : ''} ${notification ? 'open-not' : 'close-not'} ${highlighting ? 'highlight' : ''}`}>
        <div className="detailed-not-wrp">
          <p className="detailed-not-text">
            {notification}
          </p>
        </div>
        <p className="detailed-svg-text">
          <span className="detailed-svg-num">{currentLevel}</span>
          {t(T.LVL)}
        </p>
        <WidgetProgress progress={simulatedProgress} />
      </div>
      <button
        type="button"
        onClick={handleClaimClick}
        className={`detailed-text-cont ${hasClaim ? '' : 'disabled'}`}
      >
        <span className="detailed-text">
          {t(claimLimitExceeded ? T.COMPLETED : T.CLAIM)}
        </span>
      </button>
    </div>
  );
});
